import { Button, ChevronLeftIcon, useTheme } from '@itsrever/design-system'
import {
    ReturnsProductDetailsResponse,
    ReturnsProductDetailsResponseVariantInfo
} from '@itsrever/rever-api'
import React, { useEffect, useMemo } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getGridPosition } from '@/components/FullCatalog/utils'
import { QuantitySelector } from '@/ui/components/quantity-selector'
import { animateCartItemAddition } from '@/components/FullCatalog/Components/Animations'
import { usePostHog } from '@/application/use-posthog'
import { useAppSelector } from '@/redux/hooks'
import { useCart } from '@/application/use-cart/use-cart'
import { ProductDescription } from '@/app/full-catalog/product-details/product-description'
import { ProductVariantSelector } from '@/app/full-catalog/product-details/product-variant-selector'
import { Skeleton } from '@heroui/react'
import { ProductImages } from '@/app/full-catalog/product-details/product-images'

const hasStock = (variant: ReturnsProductDetailsResponseVariantInfo) => {
    return (
        !variant.is_inventory_enabled ||
        (variant?.inventory_quantity !== undefined &&
            variant?.inventory_quantity >= 1)
    )
}

interface MobileVariantSelectorProps {
    productId: string
    selectedCategoryIndex: number
    returnedItemId: string
    name: string
    product: ReturnsProductDetailsResponse
    onClose: () => void
    loadingDetails: boolean
    photoList: Array<string>
    mainImage: string
}

export const MobileVariantSelector: React.FC<MobileVariantSelectorProps> = ({
    productId,
    selectedCategoryIndex,
    returnedItemId,
    name,
    product,
    onClose,
    photoList,
    loadingDetails,
    mainImage
}) => {
    const { t } = useTranslation()

    const theme = useTheme()

    const { addItemToCart } = useCart()

    const { captureEventPosthog } = usePostHog()

    const [selectedQuantity, setSelectedQuantity] = useState<number>(1)

    const [displayImage, setDisplayImage] = useState<string>('')

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const isMisako = settings.ecommerce_group === 'misako'
    const isHoff = settings.ecommerce_group === 'hoff'

    useEffect(() => {
        setDisplayImage(
            mainImage
                ? mainImage
                : (product.product_info?.featured_image_url ??
                      photoList[0] ??
                      '')
        )
    }, [mainImage, product.product_info?.featured_image_url, photoList])

    const variants = product.product_info?.variants ?? []

    const [selectedVariant, setSelectedVariant] = useState<
        ReturnsProductDetailsResponseVariantInfo | undefined
    >(undefined)

    const sortedVariantsByStock = useMemo(() => {
        const variants = product.product_info?.variants ?? []

        const sortedVariants = variants.sort((a, b) => {
            const aHasStock = hasStock(a)
            const bHasStock = hasStock(b)

            if (aHasStock === bHasStock) return 0
            return aHasStock ? -1 : 1
        })

        return sortedVariants
    }, [product.product_info?.variants])

    const onChangeVariant = (variantId: string) => {
        const variant = variants.find(
            (variant) => variant.platform_id === variantId
        )

        setSelectedVariant(variant)
        variant?.image_urls?.[0] && changeMainImage(variant?.image_urls?.[0])
    }

    const maxQuantity =
        selectedVariant && hasStock(selectedVariant)
            ? selectedVariant.inventory_quantity
            : 1

    const onChangeQuantity = (newQuantity: number) => {
        setSelectedQuantity(newQuantity)
    }

    const changeMainImage = (newImage: string) => {
        setDisplayImage(
            newImage ?? product.product_info?.featured_image_url ?? ''
        )
    }

    const onConfirmVariant = async () => {
        if (!isMisako && !isHoff) {
            await animateCartItemAddition()
        }
        const gridPosition = getGridPosition(productId)
        captureEventPosthog('Product added to cart', {
            row: gridPosition?.row,
            col: gridPosition?.col,
            categoryIndex: selectedCategoryIndex,
            ecommerceId: settings.ecommerce_id
        })

        selectedVariant &&
            addItemToCart(
                returnedItemId,
                selectedQuantity,
                selectedVariant,
                product
            )
        onClose()
    }

    if (isHoff) {
        return (
            <>
                <div className="absolute inset-0 p-4">
                    <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-auto">
                        <div className="flex flex-1 flex-col overflow-y-scroll">
                            <div className="shrink-0">
                                <div className="flex">
                                    <button
                                        onClick={onClose}
                                        className="mr-4 flex shrink-0 cursor-pointer items-center p-2 text-xl "
                                    >
                                        <ChevronLeftIcon
                                            color={theme.colors.grey[2]}
                                        />
                                    </button>

                                    <h4>{name}</h4>
                                </div>
                                <hr className="my-2" />
                            </div>
                            <div className="flex h-full shrink flex-col overflow-auto bg-white p-2">
                                <div className="flex w-full gap-2">
                                    {loadingDetails ? (
                                        <div className="flex h-[62vw] gap-2">
                                            <Skeleton className="size-full rounded-md md:w-80" />
                                            <div className="flex shrink-0 flex-col gap-2 overflow-auto pr-1.5">
                                                {[...Array(8)].map((_, i) => (
                                                    <Skeleton
                                                        className="size-28 shrink-0 rounded-md md:size-16"
                                                        key={i}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <ProductImages
                                            images={photoList}
                                            selectedImage={displayImage}
                                            onSelectImage={setDisplayImage}
                                        />
                                    )}
                                </div>
                                {product.product_info?.description ? (
                                    <div className="py-2">
                                        <ProductDescription
                                            description={
                                                product.product_info
                                                    ?.description
                                            }
                                        />
                                    </div>
                                ) : null}
                                <div className="my-2 flex items-center justify-between gap-1">
                                    <QuantitySelector
                                        value={selectedQuantity}
                                        onValueChange={onChangeQuantity}
                                        minValue={1}
                                        maxValue={maxQuantity}
                                    />
                                    <button
                                        onClick={onConfirmVariant}
                                        className="w-fit cursor-pointer rounded-xl bg-black p-3 font-haas text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                                    >
                                        {t('full_catalog.confirm_variant')}
                                    </button>
                                </div>
                                <p className="mb-1 mt-2">
                                    {t('full_catalog.select_option')}
                                </p>
                                <div className="mt-2 flex grow flex-col overflow-auto">
                                    {loadingDetails ? (
                                        <Skeleton className="size-full" />
                                    ) : (
                                        <ProductVariantSelector
                                            variants={sortedVariantsByStock}
                                            selectedVariantId={
                                                selectedVariant?.platform_id
                                            }
                                            onVariantSelect={onChangeVariant}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    if (isMisako) {
        return (
            <>
                <div className="absolute inset-0 p-4">
                    <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-auto">
                        <div className="flex flex-1 flex-col overflow-y-scroll">
                            <div className="shrink-0">
                                <div className="flex">
                                    <button
                                        onClick={onClose}
                                        className="mr-4 flex shrink-0 cursor-pointer items-center p-2 text-xl "
                                    >
                                        <ChevronLeftIcon
                                            color={theme.colors.grey[2]}
                                        />
                                    </button>

                                    <p className="font-[Inter] text-lg">
                                        {name}
                                    </p>
                                </div>
                            </div>
                            <div className="flex shrink flex-col overflow-auto rounded-lg bg-white p-2">
                                {loadingDetails ? (
                                    <div className="flex h-[62vw] gap-2">
                                        <Skeleton className="size-full rounded-md md:w-80" />
                                        <div className="flex shrink-0 flex-col gap-2 overflow-auto pr-1.5">
                                            {[...Array(8)].map((_, i) => (
                                                <Skeleton
                                                    className="size-28 shrink-0 rounded-md md:size-16"
                                                    key={i}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <ProductImages
                                        images={photoList}
                                        selectedImage={displayImage}
                                        onSelectImage={setDisplayImage}
                                    />
                                )}
                                <p className="mb-3 mt-4 font-[Inter]">
                                    {t('full_catalog.select_option')}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center justify-center gap-4">
                            <QuantitySelector
                                value={selectedQuantity}
                                onValueChange={onChangeQuantity}
                                minValue={1}
                                maxValue={maxQuantity}
                            />
                            <Button onClick={onConfirmVariant}>
                                {t('full_catalog.confirm_variant')}
                            </Button>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <div className="absolute inset-0 p-4">
                <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-auto">
                    <div className="flex flex-1 flex-col overflow-y-scroll">
                        <div className="shrink-0">
                            <div className="flex">
                                <button
                                    onClick={onClose}
                                    className="mr-4 flex shrink-0 cursor-pointer items-center p-2 text-xl "
                                >
                                    <ChevronLeftIcon
                                        color={theme.colors.grey[2]}
                                    />
                                </button>

                                <h4>{name}</h4>
                            </div>
                            <hr className="my-2" />
                        </div>
                        <div className="flex shrink flex-col overflow-auto bg-white">
                            {loadingDetails ? (
                                <div className="flex h-[62vw] gap-2">
                                    <Skeleton className="size-full rounded-md md:w-80" />
                                    <div className="flex shrink-0 flex-col gap-2 overflow-auto pr-1.5">
                                        {[...Array(8)].map((_, i) => (
                                            <Skeleton
                                                className="size-28 shrink-0 rounded-md md:size-16"
                                                key={i}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <ProductImages
                                    images={photoList}
                                    selectedImage={displayImage}
                                    onSelectImage={setDisplayImage}
                                />
                            )}
                            {product.product_info?.description ? (
                                <div className="pt-3">
                                    <ProductDescription
                                        description={
                                            product.product_info?.description
                                        }
                                    />
                                </div>
                            ) : null}
                            <p className="mb-3 pt-3">
                                {t('full_catalog.select_option')}
                            </p>
                            <div className="flex grow flex-col overflow-auto pr-1.5">
                                {loadingDetails ? (
                                    <div className="flex flex-col gap-2">
                                        {[...Array(5)].map((_, index) => (
                                            <Skeleton
                                                className="min-h-[60px] w-full rounded-md"
                                                key={index}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <ProductVariantSelector
                                        variants={sortedVariantsByStock}
                                        selectedVariantId={
                                            selectedVariant?.platform_id
                                        }
                                        onVariantSelect={onChangeVariant}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <ButtonContainer
                        $show={
                            selectedVariant !== undefined &&
                            hasStock(selectedVariant)
                        }
                        className="flex items-center justify-center gap-4 rounded-full bg-white px-8 py-3 shadow"
                    >
                        <QuantitySelector
                            value={selectedQuantity}
                            onValueChange={onChangeQuantity}
                            minValue={1}
                            maxValue={maxQuantity}
                        />
                        <Button onClick={onConfirmVariant}>
                            {t('full_catalog.confirm_variant')}
                        </Button>
                    </ButtonContainer>
                </div>
            </div>
        </>
    )
}

interface ButtonContainerProps {
    $show: boolean
}
const ButtonContainer = styled.div<ButtonContainerProps>`
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    bottom: ${(p) => (p.$show ? '10px' : '-200px')};
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
`
